import React, { useMemo, useState } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import uniqBy from 'lodash/uniqBy';
import { useTranslation } from 'react-i18next';

import AddSharedSender from 'actions/email_shared_senders/add_shared_sender';
import CloseModal from 'actions/modal/close_modal';
import ConfirmOrCancelModal from 'components/common/confirm_or_cancel_modal';
import connect from 'components/lib/connect';
import { normalizeEmailAddress } from 'models/customer_email_address';
import RadioGroup from 'components/common/radio_group';

export function AddSharedSenderModal({ emailAddresses, onCancel, onConfirm }) {
  const { t } = useTranslation();
  const [selectedAddress, setSelectedAddress] = useState(emailAddresses[0]);

  const options = useMemo(() => emailAddresses.map(address => ({ label: address, value: address })), [emailAddresses]);

  return (
    <ConfirmOrCancelModal
      data-aid="addSharedSender"
      onCancel={onCancel}
      onSubmit={() => onConfirm({ address: selectedAddress })}
      submitButtonDisabled={!selectedAddress}
      submitText={t('Add to Shared Senders')}
      text={t(
        'New email from this address will start its own customer and conversation instead of landing here. Replies within an existing conversation are unaffected, and nothing already on this profile changes.'
      )}
      title={t('Add to Shared Senders?')}
    >
      {emailAddresses.length > 1 ? (
        <RadioGroup
          data-aid="addSharedSender-addresses"
          onChange={evt => setSelectedAddress(evt.target.value)}
          options={options}
          value={selectedAddress}
        />
      ) : (
        <Address data-aid="addSharedSender-address">{selectedAddress}</Address>
      )}
    </ConfirmOrCancelModal>
  );
}

AddSharedSenderModal.propTypes = {
  emailAddresses: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

const Address = styled.p`
  font-weight: ${p => p.theme.fontWeight.medium};
`;

function mapStateToProps({ getProvider }) {
  const profile = getProvider('profile').get();
  const emails = (get(profile, 'emails') || []).filter(email => !!email.original);

  // Supernova normalizes before hashing, so two casings of one address are one
  // entry there and must not read as a choice here. The primary leads, matching
  // CustomerProfile.getDefaultEmail, since confirming without re-reading is the
  // common case.
  const primaryFirst = [...emails].sort((a, b) => Boolean(b.primary) - Boolean(a.primary));

  return {
    emailAddresses: uniqBy(primaryFirst, email => normalizeEmailAddress(email.original)).map(email => email.original),
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onCancel: () => executeAction(CloseModal),
    onConfirm: ({ address }) => executeAction(AddSharedSender, { address }),
  };
}

export default connect(mapStateToProps, mapExecuteToProps)(AddSharedSenderModal);
